/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// All services hub (redesign artboard 02). QuickPods services live in this
// portal; TrainPods and Inference cards link out to their own portals.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { managedServiceApi, sshKeyService, templateService } from '../services/api';
import { isFileStorageEnabled, isManagedMLOpsEnabled, isSandboxEnabled } from '../config/featureFlags';
import { getConsoleNav, TRAINING_PORTAL_URL, INFERENCE_PORTAL_URL } from '../config/consoleNav';
import { useSelectedProject } from '../hooks/useSelectedProject';
import type { CatalogOffering } from '../types/managedServices';
import { Page, PageHeader, Chip, LinkButton, formatINR } from '../components/console/ui';
import {
  ChipIc, TerminalIc, DriveIc, LayersIc, TrendIc, ServersIc, BoltIc, ChatIc, KeyIc, DbIc, ExternalIc,
} from '../components/console/icons';
import ServiceCard, { ServiceCardProps } from '../components/services/ServiceCard';
import { useAccountOverview, formatGb } from '../components/home/useAccountOverview';

type Category = 'all' | 'quick' | 'train' | 'inf' | 'storage' | 'dev' | 'more';

interface Section {
  id: Exclude<Category, 'all'>;
  chip: string;
  title: string;
  blurb?: string;
  action?: { label: string; to: string };
  cards: Array<ServiceCardProps & { id: string }>;
}

interface ToolLink {
  id: string;
  title: string;
  sub: React.ReactNode;
  to?: string;
}

const DOCS_URL = 'https://docs.podstack.ai';
const API_REFERENCE_URL = 'https://cloud.podstack.ai/auth/swagger/index.html';

const asList = (data: any, ...keys: string[]): any[] => {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  return [];
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const toolCls =
  'ps-card p-4 flex flex-col gap-1 min-h-[44px] hover:border-ps-accent-line hover:bg-ps-hover transition-colors text-ps-text';

const Services: React.FC = () => {
  const [cat, setCat] = useState<Category>('all');
  const ov = useAccountOverview();
  const { selectedProjectId } = useSelectedProject();
  const mlopsOn = isManagedMLOpsEnabled();
  const sandboxesOn = isSandboxEnabled();

  const { data: catalogData } = useQuery({
    queryKey: ['managed-services-catalog'],
    queryFn: () => managedServiceApi.getCatalog().then((res) => res.data),
    enabled: mlopsOn,
  });
  const { data: templatesData } = useQuery({
    queryKey: ['my-templates', selectedProjectId],
    queryFn: () =>
      (selectedProjectId
        ? templateService.getProjectTemplates(selectedProjectId)
        : templateService.getMyTemplates()
      ).then((res: any) => res.data),
  });
  const { data: keysData } = useQuery({
    queryKey: ['ssh-keys', selectedProjectId],
    queryFn: () => sshKeyService.list(selectedProjectId || undefined).then((res) => res.data),
  });

  const cheapestOffering = useMemo(() => {
    const rows = asList(catalogData, 'data', 'offerings') as CatalogOffering[];
    return rows.filter((o) => o.monthly_price > 0).sort((a, b) => a.monthly_price - b.monthly_price)[0];
  }, [catalogData]);
  const customTemplates = asList(templatesData, 'data', 'templates').filter((t: any) => t.scope === 'custom');
  const sshKeys = asList(keysData, 'data', 'keys');

  const { gpus, currency } = ov;
  const gpuFrom = gpus.cheapest ? `${formatINR(gpus.cheapest.perHour, currency, 0)}/hr · ${gpus.cheapest.type}` : null;
  const podsNow = [
    `${ov.runningPods} running`,
    gpus.freeTypes.length > 0 ? `${gpus.freeTypes.slice(0, 2).join(', ')} in stock` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const sections: Section[] = [
    {
      id: 'quick',
      chip: 'QuickPods',
      title: 'QuickPods',
      blurb: `GPU cloud - pods${sandboxesOn ? ', sandboxes' : ''}, NFS volumes${mlopsOn ? ' and managed MLOps' : ''}.`,
      action: { label: 'Launch pod', to: '/pods/create' },
      cards: [
        {
          id: 'pods',
          icon: <ChipIc />,
          title: 'GPU Pods',
          description: 'Containers on dedicated NVIDIA GPUs with Jupyter, ComfyUI and other templates. Billed per minute.',
          tag: gpus.hasAvailabilityData
            ? gpus.freeTypes.length > 0
              ? { tone: 'ok', label: 'Available' }
              : { tone: 'warn', label: 'All GPUs in use' }
            : null,
          facts: [
            ...(gpus.types.length ? [{ k: 'GPUs', v: gpus.types.join(', ') }] : []),
            ...(gpuFrom ? [{ k: 'From', v: gpuFrom }] : []),
            { k: 'Now', v: podsNow },
          ],
          actions: [
            { label: 'Launch pod', to: '/pods/create', primary: true },
            { label: 'Open', to: '/pods' },
          ],
        },
        ...(sandboxesOn
          ? [
              {
                id: 'sandboxes',
                icon: <TerminalIc />,
                title: 'Sandboxes',
                description: 'Short-lived, isolated containers for running untrusted or agent-generated code via API.',
                tag:
                  ov.runningSandboxes > 0
                    ? { tone: 'ok' as const, label: `${ov.runningSandboxes} running` }
                    : { tone: 'off' as const, label: 'Idle' },
                facts: [
                  { k: 'Now', v: `${ov.runningSandboxes} running` },
                  { k: 'History', v: plural(ov.sandboxes.length, 'sandbox', 'sandboxes') },
                ],
                actions: [
                  { label: 'Create sandbox', to: '/sandboxes/create', primary: true },
                  { label: 'Open', to: '/sandboxes' },
                ],
              },
            ]
          : []),
        {
          id: 'volumes',
          icon: <DriveIc />,
          title: 'NFS volumes',
          description: 'Persistent file storage that mounts into GPU Pods and survives restarts. QuickPods only.',
          tag:
            ov.volumes.length > 0
              ? { tone: 'ok', label: `${ov.volumes.length} active` }
              : { tone: 'off', label: 'None' },
          facts: [
            {
              k: 'Now',
              v:
                ov.volumes.length > 0
                  ? `${formatGb(ov.volumeGb)} across ${plural(ov.volumes.length, 'volume')}`
                  : 'No volumes yet',
            },
            { k: 'Billing', v: 'Monthly, by quota' },
          ],
          actions: [{ label: ov.volumes.length > 0 ? 'Manage volumes' : 'Create volume', to: '/volumes', primary: true }],
        },
        ...(mlopsOn
          ? [
              {
                id: 'mlops',
                icon: <LayersIc />,
                title: 'Managed MLOps',
                description: 'Provisioned, patched and billed for you: experiment tracking, pipelines and data versioning.',
                tag:
                  ov.activeManaged > 0
                    ? { tone: 'ok' as const, label: `${ov.activeManaged} active` }
                    : { tone: 'off' as const, label: 'None active' },
                facts: [
                  { k: 'Services', v: 'MLflow · Kubeflow · DVC · Metaflow' },
                  ...(cheapestOffering
                    ? [
                        {
                          k: 'From',
                          v: `${formatINR(cheapestOffering.monthly_price / 100, cheapestOffering.currency, 0)}/mo · ${
                            cheapestOffering.tier.charAt(0).toUpperCase() + cheapestOffering.tier.slice(1)
                          }`,
                        },
                      ]
                    : []),
                  { k: 'Billing', v: 'Monthly, renews' },
                ],
                actions: [
                  { label: 'Order service', to: '/managed-mlops', primary: true },
                  { label: 'Instances', to: '/managed-mlops/instances' },
                ],
              },
            ]
          : []),
      ],
    },
    {
      id: 'train',
      chip: 'TrainPods',
      title: 'TrainPods',
      blurb: 'Training studio - hourly GPUs, driven from the podstack CLI. Opens the TrainPods console.',
      action: { label: 'Launch a GPU', to: `${TRAINING_PORTAL_URL}/console/gpus` },
      cards: [
        {
          id: 'train-gpus',
          icon: <TrendIc />,
          title: 'GPU catalog',
          description: 'Compare GPUs by VRAM, region and hourly price, then launch on demand.',
          facts: [{ k: 'Pricing', v: 'Hourly · live in the catalog' }],
          actions: [{ label: 'Browse GPUs', to: `${TRAINING_PORTAL_URL}/console/gpus` }],
        },
        {
          id: 'train-instances',
          icon: <ServersIc />,
          title: 'Instances & CLI',
          description: 'Track running training instances and connect over SSH. Keys stay on your machine.',
          facts: [
            { k: 'Connect', v: 'podstack gpu instances ssh', mono: true },
            { k: 'Auth', v: 'API key (psk_…)' },
          ],
          actions: [
            { label: 'Open instances', to: `${TRAINING_PORTAL_URL}/console/instances` },
            { label: 'CLI docs', to: DOCS_URL },
          ],
        },
      ],
    },
    {
      id: 'inf',
      chip: 'Inference',
      title: 'Inference',
      blurb: 'Serverless models - one OpenAI-compatible API, priced per token. Opens the Inference console.',
      action: { label: 'Get API key', to: `${INFERENCE_PORTAL_URL}/console/keys` },
      cards: [
        {
          id: 'inf-models',
          icon: <BoltIc />,
          title: 'Models',
          description: 'One OpenAI-compatible endpoint for open and frontier models, priced per token in INR.',
          facts: [{ k: 'Billing', v: 'Per token, from your wallet' }],
          actions: [{ label: 'Browse models', to: `${INFERENCE_PORTAL_URL}/console/models` }],
        },
        {
          id: 'inf-playground',
          icon: <ChatIc />,
          title: 'Playground',
          description: 'Try any model in the browser and see token cost per message before you write code.',
          facts: [],
          actions: [{ label: 'Open playground', to: `${INFERENCE_PORTAL_URL}/console/playground` }],
        },
        {
          id: 'inf-keys',
          icon: <KeyIc />,
          title: 'API keys & usage',
          description: 'Per-key limits and data controls, with spend per model.',
          facts: [{ k: 'Endpoint', v: '/infer/v1/chat/completions', mono: true }],
          actions: [{ label: 'Manage keys', to: `${INFERENCE_PORTAL_URL}/console/keys` }],
        },
      ],
    },
    {
      id: 'storage',
      chip: 'Storage',
      title: 'Storage',
      blurb: 'S3-compatible object storage for datasets, checkpoints and outputs.',
      cards: [
        {
          id: 'objects',
          icon: <DbIc />,
          title: 'Object storage',
          description: 'S3-compatible buckets per project for datasets, checkpoints and outputs.',
          tag:
            ov.buckets.length > 0
              ? { tone: 'ok', label: `${ov.buckets.length} active` }
              : { tone: 'off', label: 'Empty' },
          facts: [
            { k: 'Now', v: plural(ov.buckets.length, 'bucket') },
            { k: 'Access', v: 'S3 API + web browser' },
          ],
          actions: [{ label: ov.buckets.length > 0 ? 'Open buckets' : 'Create bucket', to: '/drive', primary: true }],
        },
        ...(isFileStorageEnabled()
          ? [
              {
                id: 'files',
                icon: <DbIc />,
                title: 'File storage',
                description: 'Browse and manage files stored in your project.',
                facts: [],
                actions: [{ label: 'Open', to: '/storage' }],
              },
            ]
          : []),
      ],
    },
  ];

  const tools: ToolLink[] = [
    { id: 'advisor', title: 'Compute Advisor', sub: 'Ranks QuickPods + TrainPods GPUs for your job', to: '/advisor' },
    {
      id: 'templates',
      title: 'Launch templates',
      sub: customTemplates.length ? `${customTemplates.length} saved` : 'Save a pod config to reuse',
      to: '/pods/templates',
    },
    {
      id: 'keys',
      title: 'SSH keys & API tokens',
      sub: sshKeys.length ? plural(sshKeys.length, 'SSH key') : 'Add a key to SSH into pods',
      to: '/settings/access-keys',
    },
    { id: 'cli', title: 'podstack CLI', sub: 'Install, auth, SSH into GPUs', to: DOCS_URL },
    { id: 'api', title: 'REST API reference', sub: 'Swagger · opens in new tab', to: API_REFERENCE_URL },
  ];

  // Flag-gated extras (VMs, Baremetal, Notebooks…) - same list the sidebar shows under "More".
  const more = getConsoleNav().find((g) => g.id === 'more')?.items || [];

  const counts: Record<Exclude<Category, 'all'>, number> = {
    quick: sections[0].cards.length,
    train: sections[1].cards.length,
    inf: sections[2].cards.length,
    storage: sections[3].cards.length,
    dev: tools.length,
    more: more.length,
  };
  const shown = (id: Exclude<Category, 'all'>) => cat === 'all' || cat === id;

  const chips: Array<{ id: Category; label: string }> = [
    { id: 'all', label: 'All' },
    ...sections.map((s) => ({ id: s.id, label: s.chip })),
    { id: 'dev', label: 'Tools' },
    ...(more.length ? [{ id: 'more' as const, label: 'More' }] : []),
  ];

  return (
    <Page>
      <PageHeader
        title="All services"
        subtitle="Everything Podstack runs, in one place. All services bill from your wallet in INR."
      />

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
        {chips.map((c) => (
          <Chip key={c.id} on={cat === c.id} onClick={() => setCat(c.id)}>
            {c.label}
            {c.id !== 'all' && <span className="text-ps-subtle">{counts[c.id as Exclude<Category, 'all'>]}</span>}
          </Chip>
        ))}
      </div>

      {sections.map(
        (s) =>
          shown(s.id) && (
            <section key={s.id} className="flex flex-col gap-4" aria-labelledby={`svc-${s.id}`}>
              <div className="flex flex-wrap justify-between items-end gap-3">
                <div className="min-w-0">
                  <h2 id={`svc-${s.id}`} className="m-0 text-xl font-medium">
                    {s.title}
                  </h2>
                  {s.blurb && <p className="mt-1 mb-0 text-sm text-ps-muted">{s.blurb}</p>}
                </div>
                {s.action && (
                  <LinkButton to={s.action.to} className="max-[820px]:w-full max-[820px]:min-h-[44px]">
                    {s.action.label}
                    {/^https?:/.test(s.action.to) && <ExternalIc className="w-3.5 h-3.5" aria-hidden="true" />}
                  </LinkButton>
                )}
              </div>
              <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))]">
                {s.cards.map(({ id, ...card }) => (
                  <ServiceCard key={id} {...card} />
                ))}
              </div>
            </section>
          ),
      )}

      {shown('dev') && (
        <section className="flex flex-col gap-4" aria-labelledby="svc-dev">
          <h2 id="svc-dev" className="m-0 text-xl font-medium">
            Tools - shared across all products
          </h2>
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))]">
            {tools.map((t) => {
              const body = (
                <>
                  <span className="font-semibold flex items-center gap-1.5">
                    {t.title}
                    {t.to && /^https?:/.test(t.to) && <ExternalIc className="w-3.5 h-3.5 text-ps-subtle" aria-hidden="true" />}
                  </span>
                  <span className="text-[13px] text-ps-muted">{t.sub}</span>
                </>
              );
              return t.to && /^https?:/.test(t.to) ? (
                <a key={t.id} href={t.to} target="_blank" rel="noopener noreferrer" className={toolCls}>
                  {body}
                </a>
              ) : (
                <Link key={t.id} to={t.to || '/'} className={toolCls}>
                  {body}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {more.length > 0 && shown('more') && (
        <section className="flex flex-col gap-4" aria-labelledby="svc-more">
          <h2 id="svc-more" className="m-0 text-xl font-medium">
            More
          </h2>
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))]">
            {more.map((m) => {
              const Icon = m.icon;
              return (
                <Link key={m.id} to={m.href} className={`${toolCls} !flex-row items-center gap-3`}>
                  <Icon className="w-5 h-5 text-ps-accent flex-none" />
                  <span className="font-semibold">{m.name}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </Page>
  );
};

export default Services;
